/**
 * RFM Data Hooks
 *
 * Custom React Query hooks for fetching RFM analytics data.
 * Provides automatic caching, loading states, and error handling.
 * Admin/SuperAdmin access only.
 */

import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { useAuthenticatedApi } from "@x-veloper/dataslot-auth";
import { RFMAnalyticsService } from "../services/rfm-analytics";
import type {
  RFMAnalysisResponse,
  SegmentCustomersData,
  RFMSegment
} from "@/shared/types/rfm-analytics";

/**
 * Hook to fetch complete RFM analysis with all 11 segments
 *
 * @param params - Optional period, limit, and preview parameters
 * @param options - React Query options
 *
 * @example
 * const { data, isLoading, error } = useRFMAnalysis({
 *   period: "2025-09",
 *   limit: 50,
 *   previewAs: "salespersonId" // Admin preview mode
 * });
 */
export function useRFMAnalysis(
  params?: {
    period?: string; // YYYY-MM format
    limit?: number; // Max customers per segment
    previewAs?: string; // Admin preview mode
  },
  options?: Omit<UseQueryOptions<RFMAnalysisResponse>, 'queryKey' | 'queryFn'>
) {
  const api = useAuthenticatedApi();
  const service = new RFMAnalyticsService(api);

  return useQuery<RFMAnalysisResponse>({
    queryKey: ['rfmAnalysis', params],
    queryFn: () => service.getRFMAnalysis(params),
    staleTime: 5 * 60 * 1000, // 5 minutes (RFM data changes slowly)
    ...options
  });
}

/**
 * Hook to fetch paginated customer list for a specific RFM segment
 *
 * @param segmentName - One of the 11 RFM segments
 * @param params - Optional period, pagination, and preview parameters
 * @param options - React Query options
 *
 * @example
 * const { data, isLoading } = useRFMSegmentCustomers("Champions", {
 *   period: "2025-09",
 *   offset: 0,
 *   limit: 50,
 *   previewAs: "salespersonId" // Admin preview mode
 * });
 */
export function useRFMSegmentCustomers(
  segmentName: RFMSegment | undefined,
  params?: {
    period?: string;
    offset?: number;
    limit?: number;
    previewAs?: string; // Admin preview mode
  },
  options?: Omit<UseQueryOptions<SegmentCustomersData>, 'queryKey' | 'queryFn'>
) {
  const api = useAuthenticatedApi();
  const service = new RFMAnalyticsService(api);

  return useQuery<SegmentCustomersData>({
    queryKey: ['rfmSegmentCustomers', segmentName, params],
    queryFn: () => {
      if (!segmentName) {
        throw new Error("Segment name is required");
      }
      return service.getSegmentCustomers(segmentName, params);
    },
    enabled: !!segmentName && (options?.enabled !== false),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options
  });
}

/**
 * Helper hook to get current period in YYYY-MM format
 * Reuses the existing implementation from useSalesData
 */
export function useCurrentPeriod() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1; // JavaScript months are 0-indexed

  return `${year}-${String(month).padStart(2, '0')}`;
}

/**
 * Helper hook to format period for display
 * Reuses the existing implementation from useSalesData
 */
export function useFormatPeriod(period?: string) {
  if (!period) return null;

  const [year, month] = period.split('-').map(Number);
  if (!year || !month) return null;

  const date = new Date(year, month - 1);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
}

/**
 * Helper hook to get RFM segment display metadata
 * Returns color, icon, and recommendations for a segment
 *
 * @param segmentName - RFM segment name
 *
 * @example
 * const metadata = useRFMSegmentMetadata("Champions");
 * // Returns: { color: "bg-green-600", icon: "Trophy", ... }
 */
export function useRFMSegmentMetadata(segmentName: RFMSegment) {
  // Import segment definitions dynamically to avoid circular dependencies
  const { SEGMENT_DEFINITIONS } = require("@/shared/lib/rfm-constants");

  return SEGMENT_DEFINITIONS[segmentName];
}

/**
 * Helper hook to get all valid RFM segment names
 * Useful for dropdowns and validation
 *
 * @example
 * const segments = useRFMSegmentNames();
 * // Returns: ["Champions", "Loyal Customers", ...]
 */
export function useRFMSegmentNames(): RFMSegment[] {
  return [
    "Champions",
    "Loyal Customers",
    "Potential Loyalists",
    "New Customers",
    "Promising",
    "Need Attention",
    "About to Sleep",
    "At Risk",
    "Can't Lose Them",
    "Hibernating",
    "Lost"
  ];
}
